// frontend/src/pages/BackupAdminPage.js
// Componente para la página de administración de copias de seguridad.
// AHORA con cierre de sesión proactivo después de una restauración.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import { exportBackup, importBackup, listBackups, restoreFromBackup } from '../api/backup';
import LoadingSpinner from '../components/LoadingSpinner';
import CustomConfirmDialog from '../components/CustomConfirmDialog';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, ArrowLeftIcon, CircleStackIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

function BackupAdminPage({ onBack }) {
  const { token } = useAuthStore();
  const { logout } = useAuth();
  const { setIsLoading, isLoading, showTemporaryMessage } = useUIStore();
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [availableBackups, setAvailableBackups] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const fileInputRef = useRef(null);

  const fetchAvailableBackups = useCallback(async () => {
    setIsLoading(true);
    try {
      const backups = await listBackups(token);
      setAvailableBackups(backups);
    } catch (error) {
      showTemporaryMessage(error.message || 'Error al cargar la lista de backups.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, showTemporaryMessage, token]);

  useEffect(() => {
    fetchAvailableBackups();
  }, [fetchAvailableBackups]);

  const handleDownloadBackup = async () => {
    setIsLoading(true);
    try {
      const blob = await exportBackup(token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map-backup-${new Date().toISOString()}.zip`; 
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showTemporaryMessage('Backup descargado con éxito.', 'success');
    } catch (error) {
      showTemporaryMessage(error.message || 'Error al descargar el backup.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleImportBackup = async () => {
    if (!selectedFile) {
      showTemporaryMessage('Por favor, selecciona un archivo para restaurar.', 'error');
      return;
    }
    
    const formData = new FormData();
    formData.append('backupFile', selectedFile);

    setIsLoading(true);
    try {
      const response = await importBackup(formData, token);
      // CAMBIO: Mostrar mensaje por 5 segundos y luego cerrar sesión.
      showTemporaryMessage(response.message || 'Restauración completada. Su sesión se cerrará en 5 segundos.', 'success', 5000);
      setTimeout(() => {
        logout();
        window.location.reload(); // Forzar recarga para una limpieza completa del estado.
      }, 5000);
    } catch (error) {
      showTemporaryMessage(error.message || 'Error al restaurar el backup.', 'error');
      setIsLoading(false); // Asegurarse de detener la carga en caso de error.
    } finally {
      // No ocultar el spinner aquí, ya que la página se recargará o el usuario será deslogueado.
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setShowConfirm(false);
    }
  };

  const handleRestoreFromNamedBackup = async (filename) => {
    setIsLoading(true);
    try {
        const response = await restoreFromBackup(filename, token);
        // CAMBIO: Mostrar mensaje por 5 segundos y luego cerrar sesión.
        showTemporaryMessage(response.message || `Reversión a "${filename}" completada. Su sesión se cerrará en 5 segundos.`, 'success', 5000);
        setTimeout(() => {
            logout();
            window.location.reload();
        }, 5000);
    } catch (error) {
        showTemporaryMessage(error.message || 'Error al revertir al backup.', 'error');
        setIsLoading(false);
    } finally {
        setShowConfirm(false);
    }
  }

  const openConfirmation = (actionType, data) => {
    if (actionType === 'import') {
        setConfirmMessage('¡ADVERTENCIA! Esta acción reemplazará TODOS los datos actuales (incluyendo usuarios y archivos) con el contenido del archivo .zip. Se creará un backup automático del estado actual. ¿Deseas continuar?');
        setConfirmAction(() => () => handleImportBackup());
    } else if (actionType === 'revert') {
        setConfirmMessage(`¿Estás seguro de que quieres revertir toda la base de datos a la versión "${data.filename}"? Se creará un backup del estado actual antes de proceder.`);
        setConfirmAction(() => () => handleRestoreFromNamedBackup(data.filename));
    }
    setShowConfirm(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto my-8">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h2 className="text-3xl font-extrabold text-gray-900 flex items-center">
          <CircleStackIcon className="h-8 w-8 mr-3" />
          Gestión de Copias de Seguridad
        </h2>
        <button onClick={onBack} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md shadow-sm hover:bg-gray-300 flex items-center space-x-2">
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Volver</span>
        </button>
      </div>

      {isLoading && <div className="flex justify-center my-4"><LoadingSpinner /></div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Sección de Descargar e Importar */}
        <div className="space-y-6">
            {/* Descargar */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Descargar Backup Completo</h3>
                <p className="text-sm text-gray-700 mb-4">Crea y descarga un archivo .zip con todos los datos y archivos de la aplicación.</p>
                <button onClick={handleDownloadBackup} className="w-full px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 flex items-center justify-center space-x-2">
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Descargar Backup (.zip)</span>
                </button>
            </div>
            {/* Restaurar */}
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="text-xl font-bold text-red-900 mb-2">Restaurar desde Archivo</h3>
                <p className="text-sm text-red-700 mb-4">
                    <strong>¡Acción destructiva!</strong> Reemplaza todos los datos actuales con los del archivo .zip.
                </p>
                <div className="flex items-center space-x-2">
                    <input type="file" accept=".zip" onChange={handleFileChange} ref={fileInputRef} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"/>
                </div>
                {selectedFile && 
                    <button onClick={() => openConfirmation('import')} className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded-md shadow-sm hover:bg-red-700 flex items-center justify-center space-x-2">
                        <ArrowUpTrayIcon className="h-5 w-5" />
                        <span>Restaurar desde {selectedFile.name}</span>
                    </button>
                }
            </div>
        </div>
        {/* Sección de Puntos de Restauración */}
        <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Puntos de Restauración Automáticos</h3>
            <p className="text-sm text-gray-600 mb-4">Estos backups se crean automáticamente antes de cada importación. Puedes usarlos para revertir a un estado anterior.</p>
            <div className="h-80 overflow-y-auto border rounded-lg p-2 bg-gray-50 space-y-2">
                {availableBackups.length > 0 ? (
                    availableBackups.map(backup => (
                        <div key={backup.filename} className="bg-white p-3 rounded-md shadow-sm flex justify-between items-center">
                            <div>
                                <p className="font-semibold text-gray-800 flex items-center"><ClockIcon className="h-4 w-4 mr-2"/>{formatDate(backup.createdAt)}</p>
                                <p className="text-xs text-gray-500">{backup.filename}</p>
                            </div>
                            <button onClick={() => openConfirmation('revert', backup)} className="p-2 text-green-600 hover:bg-green-100 rounded-full" title="Revertir a esta versión">
                                <ArrowPathIcon className="h-5 w-5"/>
                            </button>
                        </div>
                    ))
                ) : (
                    <p className="text-center text-gray-500 pt-16">No hay puntos de restauración disponibles.</p>
                )}
            </div>
        </div>
      </div>

      <CustomConfirmDialog
        show={showConfirm}
        message={confirmMessage}
        onConfirm={confirmAction}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}

export default BackupAdminPage;
